import {
  BRIGHTNESS_DEFAULT, BRIGHTNESS_MAX, BRIGHTNESS_MIN,
} from '@/game/engine/lighting';
import { Icon } from '@/components/ui/Icon';
import { selectBrightness, useSettings } from '@/store/settings';

/**
 * The cave's dimmer.
 *
 * A slider rather than a light/dark toggle, because the thing being fixed is not a
 * preference between two looks — it is that a near-black cave is a different picture
 * on an OLED in a dark room, on an LCD at 30% in daylight, and through the WebView's
 * own colour handling in the Android shell. No single authored curve is right for all
 * three, and the player is the only one who can see which they have.
 *
 * ## It cannot break the game
 *
 * Both ends are bounded in `game/engine/lighting.ts`, not here: `DARK_CEILING` stops
 * the low end reaching black and `shade()` keeps the high end off a flat grey wash.
 * It also cannot be a free lamp upgrade — the brightest setting on a Candle still
 * reaches less far than the dimmest setting on a Shwe Lamp, which is pinned by a test,
 * because depth is gated on light and a slider that bought reach would be an
 * equipment stat pretending to be an accessibility control.
 *
 * Shared between Settings and the in-cave pause menu on purpose. The setting is only
 * ever judged by looking at the cave, so the place it most needs to be adjustable is
 * with the cave still on screen behind it.
 */
export function BrightnessControl({ compact = false }: { compact?: boolean }) {
  const brightness = useSettings(selectBrightness);
  const setBrightness = useSettings((s) => s.setBrightness);
  const reset = useSettings((s) => s.resetBrightness);

  /* Shown as a percentage of the default rather than the raw multiplier: "115%" is a
     number a player can act on, "1.15" is an implementation detail. */
  const pct = Math.round(brightness * 100);
  const isDefault = Math.abs(brightness - BRIGHTNESS_DEFAULT) < 0.001;

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[11px] text-ink-dim">
          <Icon name="lamp" size={13} className="text-torch-warm" aria-hidden />
          <span>Cave brightness</span>
        </span>
        <span className="flex items-center gap-2">
          <b className="tabular text-[11px] text-ink-hi">{pct}%</b>
          {!isDefault && (
            <button
              type="button"
              onClick={reset}
              className="rounded border border-[var(--hairline)] px-1.5 py-0.5
                         font-pixel text-[5.5px] tracking-wide text-ink-dim"
            >
              RESET
            </button>
          )}
        </span>
      </div>

      <input
        type="range"
        min={BRIGHTNESS_MIN}
        max={BRIGHTNESS_MAX}
        step={0.05}
        value={brightness}
        onChange={(e) => setBrightness(Number(e.target.value))}
        aria-label="Cave brightness"
        aria-valuetext={`${pct} percent`}
        className="mt-2 h-1.5 w-full cursor-pointer appearance-none rounded-full
                   accent-[var(--torch-warm)]"
        style={{
          /* The track IS the preview: dark on the left, torch-lit on the right, so
             the control shows what it does before it is dragged. */
          background:
            'linear-gradient(90deg, #14100C 0%, #4A3520 42%, var(--torch-warm) 100%)',
        }}
      />

      {!compact && (
        <p className="mt-2 text-[10.5px] leading-snug text-ink-dim">
          The cave is meant to be dark, never black. If the walls at the edge of your
          torch look like a blank screen rather than rock, turn this up.
        </p>
      )}
    </div>
  );
}
